import React from 'react';
import { Pencil, TrashBin, PersonPlus, Hourglass, Stethoscope, Video } from '@gravity-ui/icons';

import AdminCameraControls from '../AdminCameraControls/AdminCameraControls';
import CameraHealthBadge from '../CameraHealthBadge/CameraHealthBadge';
import EntityImage from '../EntityImage/EntityImage';
import { formatCameraDate } from '../../utils/formatCameraDate';

const CAMERA_DELETE_STATUSES = ['PENDING_DELETE', 'DELETED'];

const AdminItemGrid = ({
  loading,
  currentData,
  currentLevel,
  cameraImages,
  levelMap,
  managingCamera,
  restoringCamera,
  ownerMap,
  getStatusColor,
  getStatusText,
  onItemClick,
  onEditClick,
  onDeleteClick,
  onCameraAction,
  onRestoreClick,
  onDiagnosticsClick,
  onRoomCamerasClick,
  onAccessClick,
  onOpenFramesClick,
  canOpenFrames,
  highlightId,
}) => {
  if (loading) {
    return <div className="items-grid">Загрузка…</div>;
  }

  return (
    <div className="items-grid">
      {currentData.map((item) => {
        // Камера: свежий последний кадр, а пока он грузится — original камеры из списка.
        const imageUrl = currentLevel === 'cameras'
          ? (cameraImages[item.id] || item.original?.url)
          : item.image;
        const isCameraPendingOrDeleted =
          currentLevel === 'cameras' && CAMERA_DELETE_STATUSES.includes(item.status);
        const highlighted = highlightId != null && String(item.id) === String(highlightId);
        // ТЗ V5 №12: изображение объекта иерархии — через EntityImage
        // (загруженный файл по защищённой ссылке или старый внешний адрес).
        let preview;
        if (currentLevel !== 'cameras') {
          preview = (
            <EntityImage
              entityType={levelMap[currentLevel]}
              entityId={item.id}
              image={item.image}
              alt={item.name}
              placeholder="Нет изображения"
              placeholderClassName="item-image-placeholder"
            />
          );
        } else {
          preview = imageUrl ? (
            <img src={imageUrl} alt={item.name} />
          ) : (
            <div className="item-image-placeholder">Превью отключено</div>
          );
        }
        // FE-CORR-07 FE-004: превью камеры — кнопка «Просмотр кадров»
        // (клавиатура из коробки). Удалённой камере кадры не открываются.
        const showFramesButton = currentLevel === 'cameras'
          && onOpenFramesClick
          && item.status !== 'DELETED'
          && (!canOpenFrames || canOpenFrames(item));

        return (
          <div
            key={item.id}
            className={`item-card${highlighted ? ' item-card--highlight' : ''}`}
            data-admin-item-id={item.id}
            aria-current={highlighted ? 'true' : undefined}
            onClick={() => onItemClick(item, levelMap[currentLevel])}
          >
            <div className="item-image">
              {showFramesButton ? (
                <button
                  type="button"
                  className="item-image-open"
                  onClick={(event) => onOpenFramesClick(item, event)}
                  title="Просмотр кадров"
                  aria-label="Просмотр кадров"
                >
                  {preview}
                </button>
              ) : preview}
              {currentLevel === 'cameras' && (
                <div className="item-status-badge">
                  <CameraHealthBadge camera={item} compact />
                </div>
              )}
              <div className="item-actions">
                {currentLevel === 'rooms' && onRoomCamerasClick && (
                  <button
                    type="button"
                    className="diagnostics-btn"
                    onClick={(event) => onRoomCamerasClick(item, event)}
                    title="Камеры локации"
                    aria-label="Камеры локации"
                  >
                    <Video width={16} height={16} />
                  </button>
                )}
                {currentLevel === 'cameras' && onDiagnosticsClick && (
                  <button
                    className="diagnostics-btn"
                    onClick={(event) => onDiagnosticsClick(item, event)}
                    title="Диагностика"
                  >
                    <Stethoscope width={16} height={16} />
                  </button>
                )}
                {onAccessClick && (
                  <button
                    type="button"
                    className="diagnostics-btn"
                    onClick={(event) => onAccessClick(item, event)}
                    title="Доступ"
                    aria-label="Доступ"
                  >
                    <PersonPlus width={16} height={16} />
                  </button>
                )}
                <button
                  className="edit-btn"
                  onClick={(event) => onEditClick(item, event)}
                  title="Редактировать"
                >
                  <Pencil width={16} height={16} />
                </button>
                {!isCameraPendingOrDeleted && (
                  <button
                    className="delete-btn"
                    onClick={(event) => onDeleteClick(item, event)}
                    title="Удалить"
                  >
                    <TrashBin width={16} height={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="item-name" title={item.name}>{item.name}</div>

            {currentLevel === 'cameras' ? (
              <div className="item-camera-info">
                {item.type && <div className="small text-muted mb-1">{item.type}</div>}
                {item.last_error && item.status === 'ERROR' && (
                  <div className="camera-status">
                    <div className="camera-error text-danger small">
                      <small title={item.last_error}>{item.last_error}</small>
                    </div>
                  </div>
                )}
                {item.status === 'PENDING_DELETE' && (
                  <div className="camera-pending-delete small mt-2">
                    <div>Камера назначена на удаление</div>
                    <div>Удаление запрошено {formatCameraDate(item.delete_requested_at)}</div>
                    <div>Восстановить можно до {formatCameraDate(item.delete_scheduled_at)}</div>
                    {item.status_before_delete && (
                      <div>До удаления была {getStatusText(item.status_before_delete).toLowerCase()}</div>
                    )}
                    <button
                      className="btn btn-sm btn-primary mt-1"
                      onClick={(event) => onRestoreClick(item, event)}
                      disabled={restoringCamera === item.id}
                    >
                      {restoringCamera === item.id ? <Hourglass width={14} height={14} /> : 'Восстановить камеру'}
                    </button>
                  </div>
                )}
                {item.status === 'DELETED' && (
                  <div className="camera-deleted small mt-2 text-muted">
                    Удалена {formatCameraDate(item.deleted_at)}
                  </div>
                )}
                {!isCameraPendingOrDeleted && (
                  <AdminCameraControls
                    cameraId={item.id}
                    managingCamera={managingCamera}
                    onCameraAction={onCameraAction}
                  />
                )}
              </div>
            ) : (
              <div className="item-owner small text-muted mt-1">
                {ownerMap[item.owner] || item.owner || 'Владелец не указан'}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AdminItemGrid;
